import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { DictionaryItem, User } from "../../types/users.types";
import type { ModalType } from "../../pages/UsersPage";
import { createManager, deleteManager, editManager } from "../../api/users";
import { toast } from "sonner";

interface UsersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  modalType: ModalType["modalType"];
  user: User | undefined;
  roles: DictionaryItem[];
}

export function UsersModal({
  open,
  onOpenChange,
  modalType,
  user,
  roles,
}: UsersModalProps) {
  const [userRole, setUserRole] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState(""); // новый стейт
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<{
    email?: string;
    name?: string;
    surname?: string;
    role?: string;
  }>({});

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!email && !user?.login) newErrors.email = "Введите email";
    if (!name && !user?.name) newErrors.name = "Введите имя";
    if (!surname && !user?.surname) newErrors.surname = "Введите фамилию";
    if (!userRole && !roles?.find((r) => r.label === user?.role)?.code) {
      newErrors.role = "Выберите роль";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleClick = () => {
    if (modalType === "create") handleApply();
    if (modalType === "edit") handleChange();
    if (modalType === "delete") handleDelete();
  };

  const handleClose = () => {
    setIsSuccess(false);
    setErrors({});
    setName("");
    setSurname("");
    setEmail("");
    setUserRole("");
    onOpenChange(false);
  };

  const handleApply = async () => {
    if (!validate()) return;

    const payload = {
      role: userRole,
      login: email,
      name: name.trim(),
      surname: surname.trim(),
    };

    try {
      await createManager(payload);
      setIsSuccess(true);
      toast.success("Пользователь добавлен добавлен");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Не удалось добавить пользователя";
      toast.error(message);
      console.error("error", error);
    }
  };

  const handleChange = async () => {
    if (!validate()) return;
    const id = user?.id as number;
    const roleCode = roles?.find((r) => r.label === user?.role)?.code;

    const payload = {
      role: (userRole === "" ? roleCode : userRole) as string,
      name: name.trim() || (user?.name as string),
      surname: surname.trim() || (user?.surname as string),
    };

    try {
      await editManager(id, payload);
      setIsSuccess(true);
      toast.success("Пользователь обновлен добавлен");
    } catch (error) {
      toast.error("Не удалось обновить пользователя");
      console.error(error);
    }
  };

  const handleDelete = async () => {
    const id = user?.id as number;
    try {
      await deleteManager(id);
      setIsSuccess(true);
      toast.success("Пользователь удален");
    } catch (error) {
      toast.error("Не удалось удалить пользователя");
      console.error(error);
    }
  };
  const truncate = (str: string, max = 40) =>
    str.length > max ? str.slice(0, max) + "…" : str;

  const fullName = `${user?.name ?? ""} ${user?.surname ?? ""}`.trim();

  const modalTitles = {
    create: "Добавить пользователя",
    edit: "Изменить пользователя",
    delete: `Удалить доступ пользователю ${truncate(fullName)} (${
      user?.login
    })?`,
  };

  const modalSubTitles = {
    create: "Пользователю будет отправлено письмо с приглашением",
    edit: "Изменения вступят в силу сразу после сохранения",
    delete: `Удалить доступ пользователю ${truncate(fullName)} (${
      user?.login
    })?`,
  };

  const buttonLabel = {
    create: "Пригласить",
    edit: "Сохранить",
    delete: "Удалить",
  } as const;

  const successTitle = {
    create: `Добавлен пользователь ${truncate(
      `${name ?? ""} ${surname ?? ""}`
    )} ${email ? ` (${email})` : ""}`,
    edit: `Данные пользователя ${truncate(fullName)} обновлены`,
    delete: `Доступ пользователя ${truncate(fullName)} (${user?.login}) удалён`,
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[560px] w-[560px] p-6">
        {isSuccess ? (
          <div className="flex flex-col items-center text-center gap-4 py-4">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg
                className="w-7 h-7 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {successTitle[modalType]}
              </h2>
            </div>
            <div className="flex gap-3 w-full mt-2">
              <Button className="flex-1 h-10" onClick={handleClose}>
                К таблице
              </Button>
              <Button
                variant="outline"
                className="flex-1 h-10"
                onClick={handleClose}
              >
                Скрыть
              </Button>
            </div>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-xl font-semibold leading-snug break-words line-clamp-3">
                {modalTitles[modalType]}
              </DialogTitle>

              <DialogDescription className="text-sm text-gray-600">
                {modalSubTitles[modalType]}
              </DialogDescription>
              {modalType === "delete" && (
                <DialogDescription className="text-sm text-gray-600">
                  Пользователь потеряет доступ к админ-панели
                </DialogDescription>
              )}
            </DialogHeader>

            {modalType !== "delete" && (
              <>
                {/* Email */}
                <div className="space-y-2">
                  <label className="block text-sm font-normal text-gray-900">
                    Email
                  </label>
                  <Input
                    type="text"
                    placeholder="Введите email"
                    value={email || user?.login}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    className={`h-10 text-base w-full ${
                      errors.email
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }`}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Имя и Фамилия */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-normal text-gray-900">
                      Имя
                    </label>
                    <Input
                      type="text"
                      placeholder="Введите имя"
                      value={name || user?.name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setErrors((prev) => ({ ...prev, name: undefined }));
                      }}
                      className={`h-10 text-base w-full ${
                        errors.name
                          ? "border-red-500 focus-visible:ring-red-500"
                          : ""
                      }`}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-500">{errors.name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-normal text-gray-900">
                      Фамилия
                    </label>
                    <Input
                      type="text"
                      placeholder="Введите фамилию"
                      value={surname || user?.surname}
                      onChange={(e) => {
                        setSurname(e.target.value);
                        setErrors((prev) => ({ ...prev, surname: undefined }));
                      }}
                      className={`h-10 text-base w-full ${
                        errors.surname
                          ? "border-red-500 focus-visible:ring-red-500"
                          : ""
                      }`}
                    />
                    {errors.surname && (
                      <p className="text-xs text-red-500">{errors.surname}</p>
                    )}
                  </div>
                </div>

                {/* Роль */}
                <div className="space-y-2">
                  <label className="block text-sm font-normal text-gray-900">
                    Роль
                  </label>
                  <Select
                    value={
                      userRole ||
                      roles?.find((r) => r.label === user?.role)?.code
                    }
                    onValueChange={(val) => {
                      setUserRole(val);
                      setErrors((prev) => ({ ...prev, role: undefined }));
                    }}
                  >
                    <SelectTrigger
                      className={errors.role ? "border-red-500" : ""}
                    >
                      <SelectValue placeholder="Выберите роль" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles?.map((role) => (
                        <SelectItem key={role.code} value={role.code}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <p className="text-xs text-red-500">{errors.role}</p>
                  )}
                </div>
              </>
            )}

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleClick}
                variant={modalType === "delete" ? "destructive" : "default"}
                className="flex-1 h-10"
              >
                {buttonLabel[modalType]}
              </Button>
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 h-10"
              >
                Отмена
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
